const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined)?.replace(/\/$/, '')
  ?? 'https://bead-api.411341.xyz'

const WRITE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE'])

export class ApiError extends Error {
  status: number
  code: string
  details: Record<string, unknown>

  constructor(message: string, status = 0, code = 'REQUEST_FAILED', details: Record<string, unknown> = {}) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = details
  }
}

function readMessage(payload: unknown, fallback: string): { message: string; code: string; details: Record<string, unknown> } {
  if (payload && typeof payload === 'object') {
    const body = payload as Record<string, unknown>
    if (body.error && typeof body.error === 'object') {
      const error = body.error as Record<string, unknown>
      return {
        message: typeof error.message === 'string' ? error.message : fallback,
        code: typeof error.code === 'string' ? error.code : 'REQUEST_FAILED',
        details: error.details && typeof error.details === 'object' ? error.details as Record<string, unknown> : {},
      }
    }
    if (typeof body.error === 'string') return { message: body.error, code: 'REQUEST_FAILED', details: {} }
    if (typeof body.message === 'string') return { message: body.message, code: 'REQUEST_FAILED', details: {} }
  }
  return { message: fallback, code: 'REQUEST_FAILED', details: {} }
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method ?? 'GET').toUpperCase()
  if (WRITE_METHODS.has(method) && !navigator.onLine) {
    throw new ApiError('当前已断网，写入操作已阻止。联网后请手动重试。', 0, 'OFFLINE_WRITE_BLOCKED')
  }

  const headers = new Headers(init.headers)
  if (WRITE_METHODS.has(method)) {
    headers.set('X-Bead-Client', 'web-v2')
    if (!headers.has('Idempotency-Key')) headers.set('Idempotency-Key', crypto.randomUUID())
    if (!(init.body instanceof FormData) && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json')
    }
  }

  let response: Response
  try {
    response = await fetch(`${API_BASE}${path}`, {
      ...init,
      method,
      headers,
      credentials: 'include',
    })
  } catch {
    throw new ApiError('无法连接服务，请检查网络后重试。')
  }

  const payload = await response.json().catch(() => null) as unknown
  if (!response.ok) {
    const error = readMessage(payload, response.statusText || '请求失败')
    const apiError = new ApiError(error.message, response.status, error.code, error.details)
    if (response.status === 401 && path !== '/api/auth/session' && path !== '/api/auth/login') {
      window.dispatchEvent(new CustomEvent('bead:unauthorized'))
    }
    throw apiError
  }

  if (payload && typeof payload === 'object' && 'data' in payload) {
    return (payload as { data: T }).data
  }
  return payload as T
}

export async function uploadImage(file: File): Promise<string> {
  if (!navigator.onLine) throw new ApiError('当前已断网，无法上传图片。', 0, 'OFFLINE_WRITE_BLOCKED')
  const form = new FormData()
  form.append('file', file)
  const result = await api<{ url: string }>('/api/upload', { method: 'POST', body: form })
  return result.url
}

export function normalizeImageUrl(url: string | null | undefined): string | undefined {
  if (!url) return undefined
  return url.replace(/^https:\/\/pub-bead\.411341\.xyz\//, `${API_BASE}/api/images/`)
}

export const queryKeys = {
  session: ['session'] as const,
  colors: ['colors'] as const,
  inventory: ['inventory'] as const,
  projects: ['projects'] as const,
  purchases: ['purchases'] as const,
  tools: ['tools'] as const,
  topConsumed: (by: 'freq' | 'total') => ['top-consumed', by] as const,
  warnings: ['inventory-warning'] as const,
  shortages: ['draft-shortage'] as const,
  calibrations: ['calibrations'] as const,
  dashboard: ['dashboard'] as const,
  history: (code: string) => ['inventory-history', code] as const,
}

export { API_BASE }
